import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getDatabase, ref, get } from 'firebase/database';
import Navbar from './Navbar';
import fc from '../firebaseConfig';
import { Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { format } from 'date-fns';

const db = getDatabase(fc);

const fetchClient = async (clientId) => {
  const clientRef = ref(db, `clients/${clientId}`);
  const snapshot = await get(clientRef);
  if (snapshot.exists()) {
    return snapshot.val();
  }
  return null;
};

const fetchEvents = async () => {
  const eventsRef = ref(db, 'events');
  const snapshot = await get(eventsRef);
  const events = [];
  if (snapshot.exists()) {
    snapshot.forEach((childSnapshot) => {
      events.push({ id: childSnapshot.key, ...childSnapshot.val() });
    });
  }
  return events;
};

// Helper function to format time
const formatTime = (duration) => {
  const hours = Math.floor(duration); // Full hours
  const minutes = Math.round((duration - hours) * 60); // Remaining minutes
  return `${hours} godzin ${minutes} minut`;
};

const ClientDetails = () => {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [visitCount, setVisitCount] = useState(0);
  const [visitDetails, setVisitDetails] = useState([]);
  const [totalTime, setTotalTime] = useState(0); // State for total time

  useEffect(() => {
    const fetchData = async () => {
      const clientData = await fetchClient(clientId);
      if (clientData) {
        setClient(clientData);
      }

      const events = await fetchEvents();
      const clientVisits = events.filter(event => event.clientId === clientId);

      // Sort visits from newest to oldest
      clientVisits.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

      // Ensure cost is a number and calculate visit duration
      const visitDetails = clientVisits.map((event, index) => {
        const startDate = new Date(event.startDate);
        const endDate = new Date(event.endDate);
        const duration = (endDate - startDate) / (1000 * 60 * 60); // Convert duration from ms to hours
        return {
          number: index + 1,
          date: format(startDate, 'dd.MM.yyyy'),
          cost: Number(event.price), // Convert to number
          duration: duration.toFixed(2), // Duration in hours
          formattedDuration: formatTime(duration), // Duration in hours and minutes
          title: event.title // Extract the title of the service
        };
      });

      setVisitCount(clientVisits.length);
      setVisitDetails(visitDetails);

      // Calculate total time (in hours)
      const totalTime = visitDetails.reduce((sum, visit) => sum + parseFloat(visit.duration), 0);
      setTotalTime(totalTime);
    };

    fetchData();
  }, [clientId]);

  // Calculate the total cost
  const totalCost = visitDetails.reduce((sum, visit) => {
    return sum + (typeof visit.cost === 'number' ? visit.cost : 0);
  }, 0);

  if (!client) return <div>Loading...</div>;

  return (
    <div>
      <Navbar />
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'auto', flexDirection: 'column', marginTop: '3rem', marginBottom: '100px'}}>
        <Paper style={{ maxWidth: '800px', padding: '20px' }}>
          <Typography variant="h4" gutterBottom>{client.name}</Typography>
          <Typography variant="h6" gutterBottom>Numer: {client.number}</Typography>
          <Typography variant="body1">Opis: {client.description}</Typography>

          {/* Table of Visit Details */}
          <TableContainer style={{ marginTop: '20px' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Numer Wizyty</TableCell>
                  <TableCell>Data Wizyty</TableCell>
                  <TableCell>Tytuł Usługi</TableCell> {/* New column for service title */}
                  <TableCell>Cena Wizyty</TableCell>
                  <TableCell>Czas Trwania</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visitDetails.map((visit) => (
                  <TableRow key={visit.number}>
                    <TableCell>{visit.number}</TableCell>
                    <TableCell>{visit.date}</TableCell>
                    <TableCell>{visit.title}</TableCell> {/* Displaying the service title */}
                    <TableCell>{visit.cost.toFixed(2)} zł</TableCell>
                    <TableCell>{visit.duration} godziny ({visit.formattedDuration})</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Total Cost and Time Information */}
          <Typography variant="h6" style={{ marginTop: '20px' }} gutterBottom>
            Liczba odbytych wizyt: {visitCount}
          </Typography>
          <Typography variant="h6" style={{ marginTop: '20px' }}>
            Łączny koszt wizyt: {totalCost.toFixed(2)} zł
          </Typography>
          <Typography variant="h6" style={{ marginTop: '20px' }}>
            Łączny czas wizyt: {totalTime.toFixed(2)} godzin ({formatTime(totalTime)})
          </Typography>
        </Paper>
      </div>
    </div>
  );
};

export default ClientDetails;
